import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppSwitch from '@nxsys/components/AppSwitch';
import Inheritable from '@nxsys/components/Inheritable';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import {
  defaultInvoiceMethodOptions,
  defaultInvoiceRaisedAfterOptions,
  defaultInvoiceRaisedMethodOptions,
  defaultInvoiceTemplateOptions,
  defaultPaymentTerm,
  defaultPayrollFreeMethod,
  defaultRuleInvoicingMethod,
  showCurrencyByCountry,
} from '@nxsys/utils';
import {
  afterDraftFeeMethodRule,
  payrollFeeMethodOptions,
} from '@nxsys/utils/formFieldValidation';
import { Col, Form, Row, Switch, Tooltip } from 'antd';
import React, { useState } from 'react';
import type { PayrollFinanceSectionProps } from './types';
import { IRELAND } from '@nxsys/store/constants/keys';

import { PayrollFeeMethodEnum } from '@nxsys/constants/AppEnums';
import { TableOutlined } from '@ant-design/icons';
import TieredFeeTableModal from '../../../invoices/components/TieredFeeTableModal';

import { AppState } from '@nxsys/store';
import { useSelector } from 'react-redux';
import { Icons } from '@nxsys/types/icons';
import { getInvoiceDemoTemplate } from '@nxsys/constants/templateFiles';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

const InvoicingBasicSection: React.FC<PayrollFinanceSectionProps> = ({
  form,
  formMode,
  watchValues,
}) => {
  const { messages } = useTypedIntl();
  // NX-5228: the domestic reverse charge is a UK construction-sector VAT
  // rule. Ireland's equivalent is RCT, handled elsewhere.
  const [openTieredFeeTable, setOpenTieredFeeTable] = useState(false);
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const toggleTieredFeeTable = () => setOpenTieredFeeTable(!openTieredFeeTable);
  const isMinimalMode = formMode === 'create';

  return (
    <div className="">
      <div className="mb-6 text-xl font-medium text-gray-800">
        {messages['common.invoicing']}
      </div>
      {!isMinimalMode && (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={messages['common.VATRegNo']}
                name={['invoicing', 'vatRegNo']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppRateInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={messages['common.VATRate']}
                name={['invoicing', 'vatRate']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppSelectSync
                  type="VATCode"
                  apiType="searchVATCode"
                  placeholder={messages['assignment.rate.selectVatRate']}
                  style={{ width: '100%' }}
                  onChangeValue={(value: any) => {
                    form.setFieldValue(['invoicing', 'vatRate'], value);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={messages['common.creditLimit']}
                name={['invoicing', 'creditLimit']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppCurrencyInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={messages['common.creditTerm']}
                name={['invoicing', 'creditTerm']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={messages['common.paymentTerm']}
            name={['invoicing', 'paymentTerm']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppSelect
              options={defaultPaymentTerm}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={messages['common.invoicingMethod']}
            name={['invoicing', 'invoicingMethod']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppSelect
              options={defaultInvoiceMethodOptions}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
      </Row>

      {!isMinimalMode && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={messages['common.invoicingEmail']}
              name={['invoicing', 'invoicingEmail']}
              rules={[
                {
                  required: true,
                },
                {
                  type: 'email',
                  message: messages['form.enterValidEmail'],
                },
              ]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <AppInput
                placeholder={messages['form.enter']}
                allowCapitalize={false}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={messages['common.ccEmail']}
              name={['invoicing', 'ccEmail']}
              rules={[
                {
                  type: 'email',
                  message: messages['form.enterValidEmail'],
                },
              ]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <AppInput
                placeholder={messages['form.enter']}
                allowCapitalize={false}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

{!isIreland && (
      <Form.Item name={['invoicing', 'domesticReverseChargeApplicable']} wrapperCol={{ span: 24 }}>
        <Inheritable<boolean>
          label={messages['common.domesticReverseChargeApplicable'] as string}
          name={['invoicing', 'domesticReverseChargeApplicable']}
          value={watchValues?.invoicing?.domesticReverseChargeApplicable}
          inheritedValue={false}
          form={form}
        >
          {({ value, onChange, disabled }) => (
            <Switch
              checked={value ?? false}
              disabled={disabled}
              onChange={onChange}
            />
          )}
        </Inheritable>
      </Form.Item>
      )}

      {!isMinimalMode && (
        <>
          <Form.Item
            label={messages['invoices.raiseInvoice']}
            name={['invoiceSetting', 'raiseInvoice']}
            labelCol={{ span: 20 }}
            wrapperCol={{ span: 4 }}
          >
            <AppSwitch />
          </Form.Item>
          {watchValues?.invoiceSetting?.raiseInvoice && (
            <>
              {/* NX-6119: PEO only adds settings to the ER cost invoice, so it
                  needs ER cost on. */}
              <Form.Item
                label={messages['invoices.raiseInvoicePEO']}
                name={['invoiceSetting', 'raiseInvoicePEO']}
                labelCol={{ span: 20 }}
                wrapperCol={{ span: 4 }}
              >
                <AppSwitch />
              </Form.Item>
              {watchValues?.invoiceSetting?.raiseInvoicePEO && (
                <>
                  <Form.Item
                    label={messages['invoices.invoiceRaisedAfter']}
                    name={['invoiceSetting', 'invoiceRaisedAfter']}
                    rules={[
                      {
                        required: true,
                      },
                    ]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <AppSelect
                      options={defaultInvoiceRaisedAfterOptions}
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                  <Form.Item
                    label={messages['invoices.invoiceRaisedMethod']}
                    name={['invoiceSetting', 'invoiceRaisedMethod']}
                    rules={[
                      {
                        required: true,
                      },
                    ]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <AppSelect
                      options={defaultInvoiceRaisedMethodOptions}
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                </>
              )}
              <Form.Item
                label={messages['common.dueDate']}
                name={['invoiceSetting', 'dueDate']}
                rules={[
                  {
                    required: true,
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const num = Number(value);
                      if (isNaN(num))
                        return Promise.reject('Due date must be a number');
                      const ruleValue =
                        watchValues?.invoiceSetting?.rule?.value;
                      if (ruleValue === 'DAYS_AFTER_THE_INVOICE_DATE') {
                        if (num < 0)
                          return Promise.reject(
                            'Number of days must be greater than 0.',
                          );
                      } else {
                        if (num < 1 || num > 31)
                          return Promise.reject(
                            'Number of days must be between 1 and 31.',
                          );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppRateInput placeholder={messages['form.enter']} />
              </Form.Item>
              <Form.Item
                label={messages['common.rule']}
                name={['invoiceSetting', 'rule']}
                rules={[
                  {
                    required: true,
                  },
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppSelect
                  options={defaultRuleInvoicingMethod}
                  placeholder={messages['form.select']}
                />
              </Form.Item>
              <Form.Item
                label={messages['invoices.invoiceForSalary']}
                name={['invoiceSetting', 'invoiceForSalary']}
                labelCol={{ span: 20 }}
                wrapperCol={{ span: 4 }}
              >
                <AppSwitch />
              </Form.Item>
              {watchValues?.invoiceSetting?.raiseInvoicePEO && (
                  <Form.Item
                    label={messages['common.VATRateForSalary']}
                    name={['invoiceSetting', 'vatSalaryRateCode']}
                    rules={[
                      {
                        required: !!watchValues?.invoiceSetting?.invoiceForSalary,
                      },
                    ]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <AppSelectSync
                      type="VATCode"
                      apiType="searchVATCode"
                      placeholder={messages['assignment.rate.selectVatRate']}
                      style={{ width: '100%' }}
                      onChangeValue={(value: any) => {
                        form.setFieldValue(
                          ['invoiceSetting', 'vatSalaryRateCode'],
                          value,
                        );
                        form.setFieldValue(
                          ['invoiceSetting', 'vatSalaryRate'],
                          Number.parseFloat(value?.fullData?.VATRate),
                        );
                      }}
                    />
                  </Form.Item>
                )}
              <Form.Item
                label={messages['invoices.invoiceForSalaryDescription']}
                name={['invoiceSetting', 'invoiceForSalaryDescription']}
                rules={[
                  {
                    required: !!watchValues?.invoiceSetting?.invoiceForSalary,
                  },
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
              <Form.Item
                label={messages['invoices.invoiceForERNI']}
                name={['invoiceSetting', 'invoiceForERNI']}
                labelCol={{ span: 20 }}
                wrapperCol={{ span: 4 }}
              >
                <AppSwitch />
              </Form.Item>
              {watchValues?.invoiceSetting?.raiseInvoicePEO && (
                  <Form.Item
                    label={messages['common.VATRateForERNI']}
                    name={['invoiceSetting', 'vatERNIRateCode']}
                    rules={[
                      {
                        required: !!watchValues?.invoiceSetting?.invoiceForERNI,
                      },
                    ]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <AppSelectSync
                      type="VATCode"
                      apiType="searchVATCode"
                      placeholder={messages['assignment.rate.selectVatRate']}
                      style={{ width: '100%' }}
                      onChangeValue={(value: any) => {
                        form.setFieldValue(
                          ['invoiceSetting', 'vatERNIRateCode'],
                          value,
                        );
                        form.setFieldValue(
                          ['invoiceSetting', 'vatERNIRate'],
                          Number.parseFloat(value?.fullData?.VATRate),
                        );
                      }}
                    />
                  </Form.Item>
                )}
              <Form.Item
                label={messages['invoices.erNIPercent']}
                name={['invoiceSetting', 'erNIPercent']}
                rules={[
                  {
                    required: !!watchValues?.invoiceSetting?.invoiceForERNI,
                  },
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '')
                        return Promise.resolve();
                      const num = Number(value);
                      if (isNaN(num))
                        return Promise.reject('ERNI % must be a number');
                      if (num < 0)
                        return Promise.reject(
                          'ERNI % must be greater than or equal to 0',
                        );
                      if (num > 100)
                        return Promise.reject(
                          'ERNI % must be less than or equal to 100',
                        );
                      return Promise.resolve();
                    },
                  },
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppRateInput placeholder={messages['form.enter']} />
              </Form.Item>
              <Form.Item
                label={messages['invoices.invoiceForERNIDescription']}
                name={['invoiceSetting', 'invoiceForERNIDescription']}
                rules={[
                  {
                    required: !!watchValues?.invoiceSetting?.invoiceForERNI,
                  },
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
              <Form.Item
                label={messages['invoices.invoiceERPension']}
                name={['invoiceSetting', 'invoiceERPension']}
                labelCol={{ span: 20 }}
                wrapperCol={{ span: 4 }}
              >
                <AppSwitch />
              </Form.Item>

              {watchValues?.invoiceSetting?.raiseInvoicePEO && (
                  <Form.Item
                    label={messages['common.VATRateForERPension']}
                    name={['invoiceSetting', 'vatERPensionRateCode']}
                    rules={[
                      {
                        required: !!watchValues?.invoiceSetting?.invoiceERPension,
                      },
                    ]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <AppSelectSync
                      type="VATCode"
                      apiType="searchVATCode"
                      placeholder={messages['assignment.rate.selectVatRate']}
                      style={{ width: '100%' }}
                      onChangeValue={(value: any) => {
                        form.setFieldValue(
                          ['invoiceSetting', 'vatERPensionRateCode'],
                          value,
                        );
                        form.setFieldValue(
                          ['invoiceSetting', 'vatERPensionRate'],
                          Number.parseFloat(value?.fullData?.VATRate),
                        );
                      }}
                    />
                  </Form.Item>
                )}
              <Form.Item
                label={messages['invoices.invoiceERPensionDescription']}
                name={['invoiceSetting', 'invoiceERPensionDescription']}
                rules={[
                  {
                    required: !!watchValues?.invoiceSetting?.invoiceERPension,
                  },
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
              <Form.Item
                label={messages['invoices.invoiceAppLevy']}
                name={['invoiceSetting', 'invoiceAppLevy']}
                labelCol={{ span: 20 }}
                wrapperCol={{ span: 4 }}
              >
                <AppSwitch />
              </Form.Item>
              {watchValues?.invoiceSetting?.raiseInvoicePEO && (
                  <Form.Item
                    label={messages['common.VATRateForAppLevy']}
                    name={['invoiceSetting', 'vatAppLevyRateCode']}
                    rules={[
                      {
                        required: !!watchValues?.invoiceSetting?.invoiceAppLevy,
                      },
                    ]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <AppSelectSync
                      type="VATCode"
                      apiType="searchVATCode"
                      placeholder={messages['assignment.rate.selectVatRate']}
                      style={{ width: '100%' }}
                      onChangeValue={(value: any) => {
                        form.setFieldValue(
                          ['invoiceSetting', 'vatAppLevyRateCode'],
                          value,
                        );
                        form.setFieldValue(
                          ['invoiceSetting', 'vatAppLevyRate'],
                          Number.parseFloat(value?.fullData?.VATRate),
                        );
                      }}
                    />
                  </Form.Item>
                )}
              <Form.Item
                label={messages['invoices.invoiceAppLevyDescription']}
                name={['invoiceSetting', 'invoiceAppLevyDescription']}
                rules={[
                  {
                    required: !!watchValues?.invoiceSetting?.invoiceAppLevy,
                  },
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
              <Form.Item
                label={messages['invoices.invoicePayrollFee']}
                name={['invoiceSetting', 'invoicePayrollFee']}
                labelCol={{ span: 20 }}
                wrapperCol={{ span: 4 }}
              >
                <AppSwitch />
              </Form.Item>
              {watchValues?.invoiceSetting?.raiseInvoicePEO && (
                  <Form.Item
                    label={messages['common.VATRateForPayrollFee']}
                    name={['invoiceSetting', 'vatPayrollFeeRateCode']}
                    rules={[
                      {
                        required: !!watchValues?.invoiceSetting?.invoicePayrollFee,
                      },
                    ]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <AppSelectSync
                      type="VATCode"
                      apiType="searchVATCode"
                      placeholder={messages['assignment.rate.selectVatRate']}
                      style={{ width: '100%' }}
                      onChangeValue={(value: any) => {
                        form.setFieldValue(
                          ['invoiceSetting', 'vatPayrollFeeRateCode'],
                          value,
                        );
                        form.setFieldValue(
                          ['invoiceSetting', 'vatPayrollFeeRate'],
                          Number.parseFloat(value?.fullData?.VATRate),
                        );
                      }}
                    />
                  </Form.Item>
                )}
              <Form.Item
                label={messages['invoices.invoicePayrollFeeDescription']}
                name={['invoiceSetting', 'invoicePayrollFeeDescription']}
                rules={[
                  {
                    required: !!watchValues?.invoiceSetting?.invoicePayrollFee,
                  },
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
              <Form.Item
                className="flex-1"
                label={messages['invoices.invoicingMethod']}
                name={['invoiceSetting', 'invoicingMethod']}
                dependencies={[
                  ['invoiceSetting', 'raiseInvoicePEO'],
                  ['invoiceSetting', 'invoiceRaisedAfter'],
                ]}
                rules={[
                  {
                    required: !!watchValues?.invoiceSetting?.invoicePayrollFee,
                  },
                  afterDraftFeeMethodRule(
                    messages,
                    ['invoiceSetting', 'raiseInvoicePEO'],
                    ['invoiceSetting', 'invoiceRaisedAfter'],
                  ),
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <div className="flex gap-[8px]">
                  <AppSelect
                    options={payrollFeeMethodOptions(
                      defaultPayrollFreeMethod,
                      watchValues?.invoiceSetting?.raiseInvoicePEO,
                      watchValues?.invoiceSetting?.invoiceRaisedAfter,
                    )}
                    placeholder={messages['form.select']}
                    value={watchValues?.invoiceSetting?.invoicingMethod?.value}
                    onChange={(value) => {
                      form.setFieldValue(
                        ['invoiceSetting', 'invoicingMethod'],
                        value,
                      );
                    }}
                  />
                  {(watchValues?.invoiceSetting?.invoicingMethod as any)
                    ?.value === PayrollFeeMethodEnum.TIERED_FEE && (
                    <div className="flex items-center">
                      <Tooltip title={messages['common.tieredFeeTable']}>
                        <TableOutlined
                          className="cursor-pointer"
                          onClick={toggleTieredFeeTable}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              </Form.Item>
              {(watchValues?.invoiceSetting?.invoicingMethod as any)?.value ===
                PayrollFeeMethodEnum.FIXED_FEE_PER_EMPLOYEE && (
                <Form.Item
                  key={'feePerEmployee'}
                  label={messages['invoices.feePerEmployee']}
                  name={['invoiceSetting', 'feePerEmployee']}
                  rules={[
                    {
                      required: true,
                      message: messages['common.pleaseEnter'],
                    },
                  ]}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <AppCurrencyInput
                    prefix={showCurrencyByCountry(payrollCountry)}
                    placeholder={messages['common.enter']}
                  />
                </Form.Item>
              )}
              {watchValues?.invoiceSetting?.raiseInvoicePEO && (
                <div className="flex items-center gap-x-[8px]">
                  <div className="flex-1">
                    <Form.Item
                      label={messages['common.invoiceTemplate']}
                      name={['invoiceSetting', 'invoiceTemplate']}
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                    >
                      <AppSelect
                        options={defaultInvoiceTemplateOptions}
                        placeholder={messages['form.select']}
                      />
                    </Form.Item>
                  </div>
                  {!!watchValues?.invoiceSetting?.invoiceTemplate?.value && (
                    <div
                      className="cursor-pointer mt-[8px]"
                      onClick={() =>
                        window.open(
                          getInvoiceDemoTemplate(
                            watchValues?.invoiceSetting?.invoiceTemplate
                              ?.value as string,
                          ),
                          '_blank',
                        )
                      }
                    >
                      <Icons.IconEye />
                    </div>
                  )}
                </div>
              )}
              <Form.Item
                name={['invoiceSetting', 'tieredFees']}
                className="hidden"
                rules={[
                  {
                    validator: (_, value) => {
                      const isTieredFee =
                        (watchValues?.invoiceSetting?.invoicingMethod as any)
                          ?.value === PayrollFeeMethodEnum.TIERED_FEE;
                      if (
                        !!watchValues?.invoiceSetting?.invoicePayrollFee &&
                        isTieredFee &&
                        (!value || value.length === 0)
                      ) {
                        return Promise.reject(
                          'At least one tiered fee is required',
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                {openTieredFeeTable && (
                  <TieredFeeTableModal
                    isOpen={openTieredFeeTable}
                    toggle={toggleTieredFeeTable}
                    defaultValues={{
                      tieredFees: watchValues?.invoiceSetting?.tieredFees,
                    }}
                    onSubmit={(values) => {
                      form.setFieldValue(
                        ['invoiceSetting', 'tieredFees'],
                        values,
                      );
                    }}
                  />
                )}
              </Form.Item>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default InvoicingBasicSection;
